import { useApolloClient } from '@apollo/react-hooks'
import PortalLearningPaths from '@classes/LearningPaths'
import PortalStudentProfile from '@classes/StudentProfile'
import { StoreProps } from '@interfaces/StoreState'
import { RegistrationMaterial } from '@interfaces/Student'
import { GET_LEARNING_PATH_BY_LPIDS } from '@queries/learningPath'
import { MY_BERLITZ_ENV, PROFILE_TYPE, PROGRAM_TYPES } from '@utils/constants'
import { decodeDDUniqueKey } from '@utils/helpers'
import {
  updateDashboardActiveMaterial,
  updateDashboardActiveProgram,
  updateDashboardActiveRegistration,
} from '@redux/actions/dashboard'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { hasPublishedScormUnits, isScormAllowed } from '../helpers'
import { getStoredMaterials, getStoredPrograms, getStoredRegistrations } from '@utils/helpers/material'

export interface UseProfessionalDevelopmentDataResult {
  loading: boolean
  LearningPaths: PortalLearningPaths
  canShowPdTab: boolean
  error?: Error
}

export const useProfessionalDevelopmentData = (): UseProfessionalDevelopmentDataResult => {
  const client = useApolloClient()
  const dispatch = useDispatch()

  const { profileInfo, selectedLP, activeRegistration, activeProgram, userPermissions } = useSelector(
    ({ userProfile, dashboard, permissions }: StoreProps) => ({
      profileInfo: userProfile?.info?.StudentProfile?.StudentProfileInfo,
      selectedLP: dashboard.activeMaterial || undefined,
      activeRegistration: dashboard.activeRegistration || undefined,
      activeProgram: dashboard.activeProgram || undefined,
      userPermissions: permissions.permissions,
    }),
    shallowEqual
  )

  const studentProfile = useMemo(() => (profileInfo ? new PortalStudentProfile(profileInfo) : undefined), [profileInfo])

  const LPModuleFlag = userPermissions['flag::LP-modules']?.show

  let activeLPID = selectedLP
  let selectedRegistrationId = activeRegistration
  let selectedProgramId = activeProgram

  if (selectedLP?.includes('|')) {
    const decoded = decodeDDUniqueKey(selectedLP)
    activeLPID = decoded?.materialLPID
    selectedRegistrationId = decoded?.registrationId
    selectedProgramId = decoded?.programId
  }

  const studentIdForQuery = useMemo(() => {
    let sfId = studentProfile?.SFId
    if (activeLPID && studentProfile) {
      let materialType: string | undefined
      if (selectedRegistrationId && selectedProgramId) {
        const regProgMaterials =
          studentProfile.RegistrationProgramMaterials?.[selectedRegistrationId]?.programs?.[selectedProgramId]
            ?.materials
        if (regProgMaterials?.[activeLPID]) materialType = regProgMaterials[activeLPID].ProgramType
      }
      if (!materialType && studentProfile.Materials?.[activeLPID]) {
        materialType = studentProfile.Materials[activeLPID].ProgramType
      }
      if (materialType && PROGRAM_TYPES.F2F_STAR.includes(materialType) && studentProfile.SFId2B) {
        sfId = studentProfile.SFId2B
      }
    }
    return sfId
  }, [studentProfile, activeLPID, selectedRegistrationId, selectedProgramId])

  const lpIdsString = useMemo(() => {
    if (!studentProfile || !activeLPID) return ''
    let match = studentProfile.Materials?.[activeLPID]
    const additionalMaterials: RegistrationMaterial[] = []
    if (!LPModuleFlag) {
      if (match?.IsElective) {
        const core = studentProfile.Materials?.[match.FoundationMaterialId!]
        if (core) additionalMaterials.push(core)
      } else if (match?.ElectiveMaterials?.length) {
        for (const lpid in studentProfile.Materials) {
          if (Object.prototype.hasOwnProperty.call(studentProfile.Materials, lpid)) {
            const material = studentProfile.Materials[lpid]
            if (match?.ElectiveMaterials?.find((e) => e.LPID === lpid)) additionalMaterials.push(material)
          }
        }
      }
    }
    return [match, ...additionalMaterials]
      .filter(Boolean)
      .map((lp) => lp?.LPID)
      .join('|')
  }, [studentProfile, activeLPID, LPModuleFlag])

  const [lpData, setLpData] = useState<any>(undefined)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | undefined>()
  const [reloadToken, setReloadToken] = useState(0)
  const lpDataRef = useRef<any>(undefined)
  const keepPreviousDataOnNextFetch = useRef(false)

  useEffect(() => {
    lpDataRef.current = lpData
  }, [lpData])

  const fetchData = useCallback(
    async (keepPreviousData = false) => {
      if (!studentProfile?.SFId || !lpIdsString) {
        if (!keepPreviousData) setLpData(undefined)
        setLoading(false)
        return
      }

      const shouldShowLoader = !keepPreviousData || !lpDataRef.current
      if (shouldShowLoader) setLoading(true)

      try {
        const result = await client.query({
          query: GET_LEARNING_PATH_BY_LPIDS,
          variables: {
            StudentId: studentIdForQuery,
            IdType: PROFILE_TYPE.STUDENT,
            Ids: lpIdsString,
            berlitzEnv: studentProfile?.MyBerlitzEnv ?? MY_BERLITZ_ENV.DEFAULT,
          },
          fetchPolicy: 'network-only',
        })
        setLpData(result.data?.getLearningPathByIds)
        setError(undefined)
      } catch (err) {
        setError(err as Error)
        if (!keepPreviousData) setLpData(undefined)
      } finally {
        if (shouldShowLoader) setLoading(false)
      }
    },
    [client, studentProfile, studentIdForQuery, lpIdsString]
  )

  useEffect(() => {
    const keepPreviousData = keepPreviousDataOnNextFetch.current
    keepPreviousDataOnNextFetch.current = false
    fetchData(keepPreviousData)
  }, [fetchData, reloadToken])

  const sfId = studentProfile?.SFId
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isProfessionalTab = window.location.hash.includes(btoa('ProfessionalDevelopmentTab'))
      if (document.hidden || !sfId || !isProfessionalTab) return
      const reg = getStoredRegistrations(sfId)
      const prog = getStoredPrograms(sfId)
      const mat = getStoredMaterials(sfId)
      if (reg !== activeRegistration) dispatch(updateDashboardActiveRegistration(reg))
      if (prog !== activeProgram) dispatch(updateDashboardActiveProgram(prog))
      if (mat !== selectedLP) dispatch(updateDashboardActiveMaterial(mat))
      keepPreviousDataOnNextFetch.current = true
      setReloadToken((t) => t + 1)
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [activeProgram, activeRegistration, dispatch, selectedLP, sfId])

  const LearningPaths = useMemo(
    () => new PortalLearningPaths(loading ? [] : lpData, studentProfile, undefined),
    [lpData, loading, studentProfile]
  )

  const isScormMaterial = useMemo(() => {
    if (!studentProfile || !selectedRegistrationId || !activeLPID) return false
    const regInfo = studentProfile.getRegistrationById(selectedRegistrationId)?.RegistrationInfo
    return regInfo?.Materials?.find((m: any) => m.LPID === activeLPID)?.IsScormMaterial === true
  }, [studentProfile, selectedRegistrationId, activeLPID])

  const canShowPdTab =
    !!hasPublishedScormUnits(LearningPaths) && isScormMaterial && isScormAllowed(studentProfile?.student)

  return { loading, LearningPaths, canShowPdTab, error }
}
